/**
 * F04 — progress must reflect confirmed work, not elapsed time.
 *
 * Server contract (GET /api/jobs/<id>, routes/health.py):
 *   {id, article_id, status: 'running'|'done'|'failed', step, error, …}
 *
 * Only `step` and `status` move a stage forward. Nothing here advances on a
 * timer, and no percentage is derived from a stage index: work that has no
 * measurable completion reports as indeterminate instead of inventing a number.
 */

/** Consecutive poll failures before we admit we cannot see the job. */
export const INTERRUPTED_AFTER = 3;

const clamp = (n) => Math.max(0, Math.min(100, Math.round(n || 0)));

/**
 * Map a backend step name onto a checklist index. Returns null for an unknown
 * or missing step — null means "no evidence", never "assume progress".
 */
export function confirmedStageIndex(jobStep, total) {
  switch (jobStep) {
    case 'uploading_video':      return Math.min(0, total - 1);
    case 'uploading_transcript': return Math.min(1, total - 1);
    case 'generating_gifs':      return Math.max(total - 2, 0);
    case 'complete':             return total;
    default:                     return null;
  }
}

/**
 * Per-stage state. A stage is 'done' only because the server reported being at
 * or past it; with no evidence nothing is done.
 */
export function stageStates({ total, confirmedIndex, status }) {
  return Array.from({ length: total }, (_, i) => {
    if (status === 'done') return 'done';
    if (confirmedIndex == null) return i === 0 ? 'active' : 'pending';
    if (i < confirmedIndex) return 'done';
    if (i === confirmedIndex) return 'active';
    return 'pending';
  });
}

/**
 * What the progress control should show.
 *   determinate   — real measured bytes (upload only)
 *   indeterminate — working, with no honest percentage available
 *   unknown       — we cannot currently see the job
 */
export function progressDisplay({ phase, uploadProgress = 0, pollInterrupted = false }) {
  if (pollInterrupted) {
    return {
      mode: 'unknown',
      label: "Can't check progress right now — the job is still running.",
    };
  }
  switch (phase) {
    case 'uploading':
      return { mode: 'determinate', pct: clamp(uploadProgress), label: `Uploading… ${clamp(uploadProgress)}%` };
    case 'processing':
      return { mode: 'indeterminate', label: 'Processing…' };
    case 'done':
      return { mode: 'determinate', pct: 100, label: 'Done' };
    case 'failed':
      return { mode: 'unknown', label: 'Processing failed' };
    default:
      return { mode: 'indeterminate', label: 'Starting…' };
  }
}

/** Track consecutive poll failures so an outage becomes visible, not silent. */
export function nextPollHealth(prev, { ok }) {
  if (ok) return { failures: 0, interrupted: false };
  const failures = (prev?.failures || 0) + 1;
  return { failures, interrupted: failures >= INTERRUPTED_AFTER };
}

/** The phase implied by the job, used to pick the display above. */
export function jobPhase({ status, uploading }) {
  if (uploading) return 'uploading';
  if (status === 'done') return 'done';
  if (status === 'failed') return 'failed';
  if (status === 'running') return 'processing';
  return 'starting';
}
